import * as React from "react";
import {
  Box,
  Card,
  Collapse,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

// One timesheet row, laid out for a phone.
//
// The weekly timesheet is a 10-column table (project, phase, seven days and an
// action) that needs roughly 1300px. Below md it either overflows the viewport
// or crushes every control, so phones get a card per row and the week becomes a
// tappable grid instead of a horizontal scroll.
export default function TimesheetEntryCard({
  row,
  index,
  weekDays = [],
  projects = [],
  phases = [],
  disabled = false,
  hoursDisabled = false,
  onProjectChange,
  onPhaseChange,
  onHourChange,
  onDescriptionChange,
  onRemove,
  // A week of filled rows is several screens of scrolling. Completed rows
  // collapse to a one-line summary so the whole week is visible at a glance;
  // whatever still needs attention stays open.
  collapsible = false,
}) {
  const rowTotal = weekDays.reduce(
    (sum, day) => sum + (parseFloat(row?.hours?.[day.dateString]) || 0),
    0
  );
  const needsDescription =
    !row?.description && (row?.projectName || row?.phaseName || rowTotal > 0);

  // Given exactly one day, the card is in the mobile daily view: a single
  // hours box instead of a week of them, and phase + hours share a line so
  // an entry is three controls rather than ten.
  const singleDay = weekDays.length === 1;

  const isComplete = Boolean(
    row?.projectName && row?.phaseName && row?.description && rowTotal > 0
  );
  const [open, setOpen] = React.useState(!collapsible || !isComplete);
  const expanded = collapsible ? open : true;

  const placeholder = (text) => (
    <Box component="span" sx={{ color: "text.secondary" }}>
      {text}
    </Box>
  );

  return (
    <Card sx={{ p: 2, mb: 1.5 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box
          onClick={collapsible ? () => setOpen((v) => !v) : undefined}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: collapsible ? "pointer" : "default",
            minWidth: 0,
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="overline" sx={{ color: "text.secondary" }}>
              Entry {index + 1}
            </Typography>
            {/* Collapsed rows still have to say what they are. */}
            {collapsible && !expanded && (
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {row?.projectName || "No project"}
                {row?.phaseName ? ` · ${row.phaseName}` : ""}
              </Typography>
            )}
          </Box>

          {collapsible && !expanded && (
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flexShrink: 0 }}>
              {rowTotal} h
            </Typography>
          )}

          {onRemove && (
            <IconButton
              size="small"
              color="error"
              onClick={(event) => {
                event.stopPropagation();
                onRemove();
              }}
              disabled={disabled}
              aria-label="Remove row"
              sx={{ flexShrink: 0 }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}

          {collapsible && (
            <IconButton size="small" sx={{ flexShrink: 0 }} aria-label={expanded ? "Collapse" : "Expand"}>
              <ExpandMoreIcon
                sx={{
                  fontSize: 20,
                  transition: "transform .2s ease",
                  transform: expanded ? "rotate(180deg)" : "none",
                }}
              />
            </IconButton>
          )}
        </Box>

        <Collapse in={expanded} unmountOnExit>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1.5 }}>

        <FormControl fullWidth size="small">
          <Select
            displayEmpty
            value={row?.projectName || ""}
            disabled={disabled}
            onChange={(event) => onProjectChange(event.target.value)}
            renderValue={(value) => value || placeholder("Select project")}
          >
            {projects
              .slice()
              .sort((a, b) => a.projectName.localeCompare(b.projectName))
              .map((project) => (
                <MenuItem key={project.projectName} value={project.projectName}>
                  {project.projectName}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <FormControl fullWidth size="small">
          <Select
            displayEmpty
            value={row?.phaseName || ""}
            disabled={disabled || !row?.projectName}
            onChange={(event) => onPhaseChange(event.target.value)}
            renderValue={(value) =>
              value ||
              placeholder(
                row?.projectName ? "Select phase" : "Select a project first"
              )
            }
          >
            {phases.length > 0 ? (
              phases
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((phase, i) => (
                  <MenuItem key={phase.id ?? i} value={phase.name}>
                    {phase.name}
                  </MenuItem>
                ))
            ) : (
              <MenuItem disabled>No phases available</MenuItem>
            )}
          </Select>
        </FormControl>

        {singleDay && (
          <TextField
            type="number"
            size="small"
            label="Hours"
            disabled={disabled || hoursDisabled}
            // New rows seed every day with 0, which filled the box with a
            // meaningless "0". Blank reads as "nothing logged yet".
            value={
              Number(row?.hours?.[weekDays[0].dateString]) > 0
                ? row.hours[weekDays[0].dateString]
                : ""
            }
            placeholder="0"
            onChange={(event) =>
              onHourChange(weekDays[0].dateString, event.target.value)
            }
            inputProps={{
              min: 0,
              step: 0.5,
              readOnly: disabled,
              inputMode: "decimal",
              style: { textAlign: "center" },
            }}
            sx={{
              width: 96,
              flexShrink: 0,
              "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                { appearance: "none", margin: 0 },
              "& input[type=number]": { MozAppearance: "textfield" },
            }}
          />
        )}
        </Box>

        {!singleDay && (
        <Box>
          <Typography
            variant="overline"
            sx={{ color: "text.secondary", display: "block", mb: 0.75 }}
          >
            Hours
          </Typography>
          {/* Four across keeps the tap target wide enough to use; seven would
              be about 45px each on a phone. */}
          <Box
            sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1 }}
          >
            {weekDays.map((day) => {
              const value = row?.hours?.[day.dateString] ?? "";
              const filled = parseFloat(value) > 0;

              return (
                <Box key={day.dateString}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "center",
                      fontWeight: 600,
                      lineHeight: 1.2,
                      color: filled ? "primary.main" : "text.secondary",
                    }}
                  >
                    {day.day}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "center",
                      color: "text.disabled",
                      fontSize: "0.65rem",
                      mb: 0.5,
                    }}
                  >
                    {day.dateString}
                  </Typography>
                  <TextField
                    type="number"
                    size="small"
                    fullWidth
                    disabled={disabled || hoursDisabled}
                    value={value}
                    onChange={(event) =>
                      onHourChange(day.dateString, event.target.value)
                    }
                    inputProps={{
                      min: 0,
                      step: 0.5,
                      readOnly: disabled,
                      // Opens the numeric keypad rather than the full keyboard.
                      inputMode: "decimal",
                      style: { textAlign: "center", padding: "8px 4px" },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        // No "transparent" fallback — that let the white card
                        // through and defeated the themed field fill.
                        ...(filled && { backgroundColor: "primary.soft" }),
                      },
                      "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                        { appearance: "none", margin: 0 },
                      "& input[type=number]": { MozAppearance: "textfield" },
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
        )}

        <TextField
          placeholder="Describe the work required (required)"
          name="description"
          fullWidth
          size="small"
          multiline
          minRows={2}
          disabled={disabled}
          value={row?.description || ""}
          onChange={(event) => onDescriptionChange(event.target.value)}
          error={Boolean(needsDescription)}
          helperText={needsDescription ? "Required before you can submit" : ""}
        />

        {/* In the daily view this would just restate the Hours box above it. */}
        {!singleDay && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 1,
              borderTop: 1,
              borderColor: "border.subtle",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Row total
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {rowTotal} h
            </Typography>
          </Box>
        )}
          </Box>
        </Collapse>
      </Box>
    </Card>
  );
}
